import { useState, useEffect, useCallback } from "react";
import { Sparkles, RefreshCw, Euro, TrendingUp, Dumbbell, X, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const eur = (n) => `€${Number(n || 0).toFixed(2).replace(".", ",")}`;

const STATUS_LABEL = {
  intake: "Intake bezig", intake_compleet: "Intake klaar", wacht_op_betaling: "Wacht op betaling",
  wordt_samengesteld: "Wordt samengesteld", geleverd: "Geleverd", samenstellen_mislukt: "MISLUKT",
};

export default function PTVerkoopTab({ token }) {
  const [data, setData] = useState(null);
  const [instellingen, setInstellingen] = useState(null);
  const [intakeView, setIntakeView] = useState(null);
  const [busy, setBusy] = useState("");
  const [melding, setMelding] = useState("");
  const auth = { Authorization: `Bearer ${token}` };

  const laad = useCallback(() => {
    fetch(`${API}/api/admin/pt/overzicht`, { headers: auth }).then(r => r.json()).then(setData).catch(() => {});
    fetch(`${API}/api/admin/pt/instellingen`, { headers: auth }).then(r => r.json()).then(setInstellingen).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { laad(); }, [laad]);

  const flash = (m) => { setMelding(m); setTimeout(() => setMelding(""), 3000); };

  const regenereer = async (orderId) => {
    if (!window.confirm("Nieuw schema samenstellen voor deze klant? Het oude schema wordt vervangen.")) return;
    setBusy(orderId);
    const r = await fetch(`${API}/api/admin/pt/regenereer/${orderId}`, { method: "POST", headers: auth });
    setBusy("");
    flash(r.ok ? "Nieuw schema wordt samengesteld (±1 min)" : "Mislukt");
    setTimeout(laad, 4000);
  };

  const opslaanInstellingen = async () => {
    setBusy("instellingen");
    const r = await fetch(`${API}/api/admin/pt/instellingen`, {
      method: "PUT", headers: { ...auth, "Content-Type": "application/json" },
      body: JSON.stringify({ prijzen: instellingen.prijzen, filosofie: instellingen.filosofie, auto_vernieuwing: instellingen.auto_vernieuwing, vernieuwing_weken: parseInt(instellingen.vernieuwing_weken) || 7 }),
    });
    setBusy("");
    flash(r.ok ? "Instellingen opgeslagen" : "Opslaan mislukt");
  };

  const verrijk = async () => {
    setBusy("verrijk");
    const r = await fetch(`${API}/api/admin/pt/verrijk-oefeningen`, { method: "POST", headers: auth });
    const d = await r.json().catch(() => ({}));
    setBusy("");
    flash(r.ok ? (d.melding || "Verrijking gestart") : "Verrijking mislukt");
  };

  const stats = data?.stats;
  return (
    <div className="space-y-6 surface-dark" data-testid="pt-verkoop-tab">
      {melding && <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl text-sm font-semibold text-black" style={{ background: GOLD }}>{melding}</div>}

      {/* Statistieken */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: <Sparkles size={15} />, label: "Verkocht", val: stats?.verkocht ?? "–" },
          { icon: <Euro size={15} />, label: "Omzet", val: stats ? eur(stats.omzet) : "–" },
          { icon: <TrendingUp size={15} />, label: "Populairste frequentie", val: stats ? `${stats.populairste_frequentie} dagen` : "–" },
          { icon: <RefreshCw size={15} />, label: "Opnieuw samengesteld", val: stats?.regeneraties ?? "–" },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-white/6 p-4" style={{ background: "#111" }}>
            <div className="flex items-center gap-2 text-[#C9A84C] mb-2">{s.icon}<span className="text-zinc-500 text-xs">{s.label}</span></div>
            <p className="text-white font-black text-xl">{s.val}</p>
          </div>
        ))}
      </div>

      {/* Meest gebruikte oefeningen */}
      {stats?.meest_gebruikte_oefeningen?.length > 0 && (
        <div className="rounded-2xl border border-white/6 p-5" style={{ background: "#111" }}>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2"><Dumbbell size={13} /> Meest gebruikte oefeningen</p>
          <div className="flex flex-wrap gap-2">
            {stats.meest_gebruikte_oefeningen.map((o, i) => (
              <span key={i} className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs">{o.naam} <span className="text-zinc-600">×{o.keer}</span></span>
            ))}
          </div>
        </div>
      )}

      {/* Instellingen */}
      {instellingen && (
        <div className="rounded-2xl border border-white/6 p-5 space-y-4" style={{ background: "#111" }}>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Prijzen & Trainingsfilosofie</p>
          <div className="grid grid-cols-5 gap-3">
            {["2", "3", "4", "5", "6"].map(d => (
              <label key={d} className="flex flex-col gap-1.5">
                <span className="text-zinc-500 text-xs text-center">{d} dagen</span>
                <input value={instellingen.prijzen?.[d] ?? ""} data-testid={`pt-prijs-${d}`}
                  onChange={e => setInstellingen(p => ({ ...p, prijzen: { ...p.prijzen, [d]: e.target.value } }))}
                  className="bg-[#0d0d0d] border border-white/10 rounded-xl px-2 py-2 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50" />
              </label>
            ))}
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-zinc-500 text-xs">Eigen trainingsfilosofie / extra regels (wordt altijd meegenomen bij het samenstellen)</span>
            <textarea rows={4} value={instellingen.filosofie || ""} data-testid="pt-filosofie"
              onChange={e => setInstellingen(p => ({ ...p, filosofie: e.target.value }))}
              placeholder="bijv. Altijd minimaal één horizontale én verticale trekoefening per week..."
              className="bg-[#0d0d0d] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50" />
          </label>
          <div className="flex items-center gap-4 flex-wrap">
            <button type="button" data-testid="pt-auto-vernieuwing-toggle"
              onClick={() => setInstellingen(p => ({ ...p, auto_vernieuwing: !p.auto_vernieuwing }))}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${instellingen.auto_vernieuwing ? "text-black border-transparent" : "bg-[#0d0d0d] text-zinc-400 border-white/10"}`}
              style={instellingen.auto_vernieuwing ? { background: GOLD } : {}}>
              Auto-vernieuwing: {instellingen.auto_vernieuwing ? "AAN" : "UIT"}
            </button>
            <label className="flex items-center gap-2 text-zinc-500 text-xs">
              Vers schema na
              <input value={instellingen.vernieuwing_weken ?? 7} data-testid="pt-vernieuwing-weken"
                onChange={e => setInstellingen(p => ({ ...p, vernieuwing_weken: e.target.value }))}
                className="w-14 bg-[#0d0d0d] border border-white/10 rounded-xl px-2 py-2 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50" />
              weken (op basis van behaalde prestaties)
            </label>
          </div>
          <div className="flex gap-3">
            <button onClick={opslaanInstellingen} disabled={busy === "instellingen"} data-testid="pt-instellingen-opslaan"
              className="px-5 py-2.5 rounded-xl font-bold text-sm text-black flex items-center gap-2 disabled:opacity-60" style={{ background: GOLD }}>
              <Save size={13} /> Opslaan
            </button>
            <button onClick={verrijk} disabled={busy === "verrijk"} data-testid="pt-verrijk-btn"
              className="px-5 py-2.5 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 text-zinc-300 disabled:opacity-60">
              {busy === "verrijk" ? "Bezig..." : "Oefening-metadata verrijken"}
            </button>
          </div>
        </div>
      )}

      {/* Verkopen */}
      <div className="rounded-2xl border border-white/6 overflow-hidden" style={{ background: "#111" }}>
        <div className="px-5 py-4 border-b border-white/5">
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Verkopen ({data?.verkopen?.length ?? 0})</p>
        </div>
        <div className="divide-y divide-white/4 max-h-[480px] overflow-y-auto">
          {(data?.verkopen || []).map(v => (
            <div key={v.order_id} className="px-5 py-3.5 flex items-center gap-4 flex-wrap" data-testid={`pt-verkoop-${v.order_id}`}>
              <div className="min-w-[160px]">
                <p className="text-white text-sm font-semibold">{v.naam}</p>
                <p className="text-zinc-600 text-xs">{v.email}</p>
              </div>
              <span className="text-zinc-500 text-xs">{v.datum}</span>
              <span className="text-zinc-400 text-xs">{v.dagen} dagen</span>
              <span className="text-xs font-bold" style={{ color: GOLD }}>{eur(v.bedrag)}</span>
              <span className={`text-xs px-2 py-1 rounded-lg ${v.status === "geleverd" ? "bg-green-500/10 text-green-400" : v.status === "samenstellen_mislukt" ? "bg-red-500/10 text-red-400" : "bg-white/5 text-zinc-400"}`}>
                {STATUS_LABEL[v.status] || v.status}
              </span>
              {v.schema_naam && <span className="text-zinc-500 text-xs italic truncate max-w-[180px]">{v.schema_naam}</span>}
              <div className="ml-auto flex gap-2">
                <button onClick={() => setIntakeView(v)} data-testid={`pt-intake-view-${v.order_id}`}
                  className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300">Intake</button>
                {(v.status === "geleverd" || v.status === "samenstellen_mislukt") && (
                  <button onClick={() => regenereer(v.order_id)} disabled={busy === v.order_id} data-testid={`pt-regenereer-${v.order_id}`}
                    className="text-xs px-3 py-1.5 rounded-lg font-semibold text-black disabled:opacity-60" style={{ background: GOLD }}>
                    {busy === v.order_id ? "..." : "Nieuw schema"}
                  </button>
                )}
              </div>
            </div>
          ))}
          {(data?.verkopen || []).length === 0 && <p className="px-5 py-8 text-zinc-600 text-sm text-center">Nog geen verkopen</p>}
        </div>
      </div>

      {/* Intake modal */}
      {intakeView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setIntakeView(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg rounded-2xl max-h-[80vh] overflow-y-auto p-6" style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)" }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-white font-bold">Intake — {intakeView.naam}</p>
              <button onClick={() => setIntakeView(null)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              {Object.entries(intakeView.intake || {}).filter(([k]) => k !== "ingevuld_op").map(([k, v]) => (
                <div key={k} className="flex gap-3 text-sm border-b border-white/4 pb-2">
                  <span className="text-zinc-500 w-44 shrink-0 capitalize">{k.replaceAll("_", " ")}</span>
                  <span className="text-zinc-200">{String(v) || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
